import React from "react";
import { Button, Form } from "react-bootstrap";
import { useSnapshot } from "valtio";
import { v4 as uuidv4 } from "uuid";
import { conversionRuleStore } from "./conversionRuleStore";
import type { ConversionRuleEntity } from "@models/conversionRuleSchema.model";
import RuleValueInput from "./RuleValueInput";

export interface Rule {
  id: string;
  entity: string;
  field: string;
  operator: string;
  value: any;
  // Selected dimension for a dimensioned EAV field (e.g. {"period":"2026SP"}).
  // Absent = "any dimension" (matches every observed cell). Compiles to a
  // `dimension.<key> == "<val>"` clause the backend binds per-cell (MPN-1202).
  dimension?: Record<string, string>;
  // "is one of" a catalog value group, by code (MPN-2328). Compiles to
  // `<entity>.<field> in value_group.<code>`; the engine resolves the list at run time.
  value_group?: string;
}

export interface RuleGroup {
  id: string;
  combinator: "AND" | "OR";
  rules: (Rule | RuleGroup)[];
}

const OPERATORS = {
  string: [
    { value: "equals", label: "equals" },
    { value: "not_equals", label: "does not equal" },
    { value: "contains", label: "contains" },
    { value: "not_contains", label: "does not contain" },
    { value: "starts_with", label: "starts with" },
    { value: "ends_with", label: "ends with" },
    { value: "in", label: "is one of" },
    { value: "is_null", label: "is empty" },
    { value: "is_not_null", label: "has value" },
  ],
  number: [
    { value: "equals", label: "=" },
    { value: "not_equals", label: "≠" },
    { value: "greater_than", label: ">" },
    { value: "greater_than_equal", label: "≥" },
    { value: "less_than", label: "<" },
    { value: "less_than_equal", label: "≤" },
    { value: "is_null", label: "is empty" },
  ],
  date: [
    { value: "equals", label: "on" },
    { value: "greater_than", label: "after" },
    { value: "less_than", label: "before" },
    { value: "is_null", label: "is empty" },
  ],
  boolean: [{ value: "equals", label: "is" }],
  choice: [
    { value: "equals", label: "is" },
    { value: "not_equals", label: "is not" },
    { value: "in", label: "is one of" },
    { value: "is_null", label: "is empty" },
  ],
};

interface ConversionRuleBuilderProps {
  rules: RuleGroup;
  invalidRuleIds?: ReadonlySet<string>;
  onChange: (rules: RuleGroup) => void;
}

export const ConversionRuleBuilder: React.FC<ConversionRuleBuilderProps> = ({
  rules,
  invalidRuleIds,
  onChange,
}) => {
  const { entities } = useSnapshot(conversionRuleStore);

  const addRule = (groupId: string) => {
    const newRule: Rule = {
      id: uuidv4(),
      entity: "",
      field: "",
      operator: "",
      value: null,
    };
    onChange(updatePath(rules, groupId, (g) => ({ ...g, rules: [...g.rules, newRule] })));
  };

  const addGroup = (parentGroupId: string) => {
    const newGroup: RuleGroup = { id: uuidv4(), combinator: "AND", rules: [] };
    onChange(
      updatePath(rules, parentGroupId, (g) => ({ ...g, rules: [...g.rules, newGroup] })),
    );
  };

  const updateRule = (ruleId: string, updates: Partial<Rule>) => {
    onChange(updateRuleInGroup(rules, ruleId, updates));
  };

  const updatePathCombinator = (groupId: string, combinator: "AND" | "OR") => {
    onChange(updatePath(rules, groupId, (g) => ({ ...g, combinator })));
  };

  const removeRule = (ruleId: string) => {
    onChange(removeRuleFromGroup(rules, ruleId));
  };

  // Groups carry an id just like conditions, so the same walk removes a group
  // and everything nested under it. The root is never reachable: it is returned
  // via the spread and only its children are filtered.
  const removeGroup = (groupId: string) => {
    onChange(removeRuleFromGroup(rules, groupId));
  };

  return (
    <div className="conversion-rule-builder">
      <RuleGroupComponent
        group={rules}
        level={0}
        entities={entities}
        invalidRuleIds={invalidRuleIds}
        onAddRule={addRule}
        onAddGroup={addGroup}
        onUpdateRule={updateRule}
        onUpdateCombinator={updatePathCombinator}
        onRemoveRule={removeRule}
        onRemoveGroup={removeGroup}
      />
    </div>
  );
};

interface RuleGroupComponentProps {
  group: RuleGroup;
  level: number;
  entities: readonly ConversionRuleEntity[];
  invalidRuleIds?: ReadonlySet<string>;
  onAddRule: (groupId: string) => void;
  onAddGroup: (groupId: string) => void;
  onUpdateRule: (ruleId: string, updates: Partial<Rule>) => void;
  onUpdateCombinator: (groupId: string, combinator: "AND" | "OR") => void;
  onRemoveRule: (ruleId: string) => void;
  onRemoveGroup: (groupId: string) => void;
}

const RuleGroupComponent: React.FC<RuleGroupComponentProps> = ({
  group,
  level,
  entities,
  invalidRuleIds,
  onAddRule,
  onAddGroup,
  onUpdateRule,
  onUpdateCombinator,
  onRemoveRule,
  onRemoveGroup,
}) => {
  const isRoot = level === 0;
  const borderColor = isRoot ? "#0d6efd" : "#dee2e6";
  const backgroundColor = isRoot ? "#f0f5ff" : level === 1 ? "#ffffff" : "#fafbfc";

  return (
    <div
      className="border rounded p-3 mb-3"
      style={{
        borderColor,
        borderWidth: isRoot ? "2px" : "1px",
        backgroundColor,
        marginLeft: level === 0 ? 0 : 24,
      }}
    >
      <div className="mb-3 d-flex align-items-center">
        <span className="text-muted fw-bold me-2" style={{ fontSize: 13 }}>
          Match
        </span>
        <Form.Select
          value={group.combinator}
          onChange={(e) => onUpdateCombinator(group.id, e.target.value as "AND" | "OR")}
          style={{ width: 80 }}
          size="sm"
        >
          <option value="AND">ALL</option>
          <option value="OR">ANY</option>
        </Form.Select>
        <span className="text-muted ms-2" style={{ fontSize: 13 }}>
          of the following:
        </span>
        {!isRoot && (
          <Button
            variant="outline-danger"
            size="sm"
            className="ms-auto"
            title="Remove group"
            aria-label="Remove group"
            onClick={() => onRemoveGroup(group.id)}
          >
            ×
          </Button>
        )}
      </div>

      <div>
        {group.rules.length === 0 && (
          <div
            className="text-muted text-center py-3 border rounded bg-light"
            style={{ fontSize: 14 }}
          >
            No conditions yet. Click "+ Add Condition" to start building your rule.
          </div>
        )}

        {group.rules.map((rule) => {
          if ("combinator" in rule) {
            return (
              <RuleGroupComponent
                key={rule.id}
                group={rule as RuleGroup}
                level={level + 1}
                entities={entities}
                invalidRuleIds={invalidRuleIds}
                onAddRule={onAddRule}
                onAddGroup={onAddGroup}
                onUpdateRule={onUpdateRule}
                onUpdateCombinator={onUpdateCombinator}
                onRemoveRule={onRemoveRule}
                onRemoveGroup={onRemoveGroup}
              />
            );
          }
          return (
            <RuleComponent
              key={rule.id}
              rule={rule as Rule}
              entities={entities}
              isInvalid={invalidRuleIds?.has(rule.id) ?? false}
              onUpdate={onUpdateRule}
              onRemove={onRemoveRule}
            />
          );
        })}
      </div>

      <hr className="my-3" />

      <div className="d-flex gap-2">
        <Button variant="outline-primary" size="sm" onClick={() => onAddRule(group.id)}>
          + Add Condition
        </Button>
        <Button variant="outline-secondary" size="sm" onClick={() => onAddGroup(group.id)}>
          + Add Group
        </Button>
      </div>
    </div>
  );
};

interface RuleComponentProps {
  rule: Rule;
  entities: readonly ConversionRuleEntity[];
  isInvalid: boolean;
  onUpdate: (ruleId: string, updates: Partial<Rule>) => void;
  onRemove: (ruleId: string) => void;
}

const RuleComponent: React.FC<RuleComponentProps> = ({
  rule,
  entities,
  isInvalid,
  onUpdate,
  onRemove,
}) => {
  const selectedEntity = entities.find((e) => e.name === rule.entity);
  const selectedField = selectedEntity?.fields.find((f) => f.name === rule.field);
  const fieldType = selectedField?.field_type || "string";
  const operators = OPERATORS[fieldType as keyof typeof OPERATORS] || OPERATORS.string;
  const dimensionKeys = selectedField?.dimension_keys ?? [];
  const dimensionNames = selectedField?.dimension_value_names ?? {};
  const dimensionOptions = selectedField?.dimensions ?? [];
  // A stored dimension the list no longer offers stays visible and flagged, never shown
  // as "Any" while the old clause keeps compiling into the rule (MPN-1673).
  const unlistedDimension = isUnlistedDimension(rule, selectedField) ? rule.dimension : undefined;

  return (
    <div
      className="border rounded p-2 mb-2 d-flex align-items-center gap-2"
      style={{
        backgroundColor: "#fff",
        borderColor: isInvalid ? "#dc3545" : undefined,
        borderWidth: isInvalid ? 2 : undefined,
      }}
    >
      <Form.Select
        value={rule.entity || ""}
        onChange={(e) =>
          onUpdate(rule.id, {
            entity: e.target.value,
            field: "",
            operator: "",
            value: null,
            value_group: undefined,
            dimension: undefined,
          })
        }
        style={{ width: 180 }}
        size="sm"
      >
        <option value="">Select entity...</option>
        {entities.map((entity) => (
          <option key={entity.name} value={entity.name}>
            {entity.display_name}
          </option>
        ))}
      </Form.Select>

      <Form.Select
        value={rule.field || ""}
        onChange={(e) =>
          onUpdate(rule.id, {
            field: e.target.value,
            operator: "",
            value: null,
            value_group: undefined,
            dimension: undefined,
          })
        }
        style={{ width: 180 }}
        disabled={!rule.entity}
        size="sm"
      >
        <option value="">Select field...</option>
        {selectedEntity?.fields.map((field) => (
          <option key={field.name} value={field.name}>
            {field.display_name}
          </option>
        ))}
      </Form.Select>

      {(dimensionOptions.length > 0 || unlistedDimension) && (
        <Form.Select
          value={canonicalizeDimension(rule.dimension)}
          isInvalid={Boolean(unlistedDimension)}
          onChange={(e) => {
            const match = dimensionOptions.find(
              (d) => canonicalizeDimension(d) === e.target.value,
            );
            // Blank => omit the dimension entirely = "any dimension" (matches
            // every observed cell) — never a silent 0 (MPN-1202).
            onUpdate(rule.id, { dimension: match ? { ...match } : undefined });
          }}
          title={`Dimension: ${dimensionKeys.join(", ") || "value"}`}
          style={{ width: 130 }}
          size="sm"
        >
          <option value="">{`Any ${dimensionKeys.join(" / ") || "value"}`}</option>
          {unlistedDimension && (
            <option value={canonicalizeDimension(unlistedDimension)} disabled>
              {`${formatDimension(unlistedDimension, dimensionKeys, dimensionNames)} — not available`}
            </option>
          )}
          {dimensionOptions.map((dim) => {
            const canonical = canonicalizeDimension(dim);
            return (
              <option key={canonical} value={canonical}>
                {formatDimension(dim, dimensionKeys, dimensionNames)}
              </option>
            );
          })}
        </Form.Select>
      )}

      <Form.Select
        value={rule.operator || ""}
        onChange={(e) => onUpdate(rule.id, { operator: e.target.value, value: null, value_group: undefined })}
        style={{ width: 120 }}
        disabled={!rule.field}
        size="sm"
      >
        <option value="">Operator...</option>
        {operators.map((op) => (
          <option key={op.value} value={op.value}>
            {op.label}
          </option>
        ))}
      </Form.Select>

      <RuleValueInput
        rule={rule}
        fieldType={fieldType}
        selectedField={selectedField}
        onUpdate={onUpdate}
      />

      <Button
        variant="outline-danger"
        size="sm"
        onClick={() => onRemove(rule.id)}
        style={{ width: 40 }}
      >
        ×
      </Button>
    </div>
  );
};

// Stable string key for a dimension dict (sorted keys) so a saved rule's
// dimension matches its picker option regardless of key order, and matches the
// backend's canonical composite key. "" == no/any dimension. (Mirrors the
// audience builder — MPN-1202.)
export function canonicalizeDimension(dim?: Record<string, string>): string {
  if (!dim || Object.keys(dim).length === 0) return "";
  const sorted = Object.fromEntries(
    Object.entries(dim).sort(([a], [b]) => a.localeCompare(b)),
  );
  return JSON.stringify(sorted);
}

// True when the rule carries a dimension its field's picker does not list — the field map
// no longer writes it or it is not a catalog code. The server refuses to save it.
export function isUnlistedDimension(
  rule: Rule,
  field?: { dimensions?: readonly Record<string, string>[] },
): boolean {
  const current = canonicalizeDimension(rule.dimension);
  if (!current || !field) return false;
  return !(field.dimensions ?? []).some((d) => canonicalizeDimension(d) === current);
}

// Human label for a dimension dict, values in declared key order — the catalog's
// Name for each code when it has one ("Fall 2026", or "Fall 2026 / North" for a
// multi-axis dimension), the code itself otherwise. The option's VALUE stays the
// code (MPN-1663).
function formatDimension(
  dim: Record<string, string>,
  keys: readonly string[],
  names: Record<string, Record<string, string>> = {},
): string {
  const orderedKeys = keys.length ? keys : Object.keys(dim);
  const label = orderedKeys
    .map((k) => (dim[k] != null && dim[k] !== "" ? names[k]?.[dim[k]] ?? dim[k] : dim[k]))
    .filter((v) => v != null && v !== "")
    .join(" / ");
  return label || JSON.stringify(dim);
}

function updatePath(
  group: RuleGroup,
  targetId: string,
  updater: (group: RuleGroup) => RuleGroup,
): RuleGroup {
  if (group.id === targetId) return updater(group);
  return {
    ...group,
    rules: group.rules.map((rule) =>
      "combinator" in rule ? updatePath(rule as RuleGroup, targetId, updater) : rule,
    ),
  };
}

function updateRuleInGroup(group: RuleGroup, ruleId: string, updates: Partial<Rule>): RuleGroup {
  return {
    ...group,
    rules: group.rules.map((rule) => {
      if ("combinator" in rule) return updateRuleInGroup(rule as RuleGroup, ruleId, updates);
      if (rule.id === ruleId) return { ...rule, ...updates };
      return rule;
    }),
  };
}

function removeRuleFromGroup(group: RuleGroup, ruleId: string): RuleGroup {
  return {
    ...group,
    rules: group.rules
      .filter((rule) => rule.id !== ruleId)
      .map((rule) =>
        "combinator" in rule ? removeRuleFromGroup(rule as RuleGroup, ruleId) : rule,
      ),
  };
}
